import { Component, computed, inject, input, linkedSignal } from "@angular/core";
import type { Signal } from "@angular/core";
import { isNil } from "lodash";
import { derivedAsync } from "ngxtension/derived-async";
import { ProgressBar } from "primeng/progressbar";
import { concat, filter, map, of, scan, switchMap, timer } from "rxjs";
import type { Observable } from "rxjs";
import { ProgressNotificationService } from "../progress-notification.service";
import type { ProgressEventWithId, ProgressMode } from "../progress.model";
import { laneBlock, laneReveal, laneSwap } from "./progress-bar.animations";

/** One sub-progress lane rendered below the total progress bar. */
export interface ProgressLane {
  key: string;
  label: string | null;
  progress: number | null;
  message: string | null;
  /** The lane's operation has finished; it stays visible (see {@link LANE_LINGER_MS}) to keep the dialog height stable. */
  done: boolean;
  /**
   * Identifies the operation run shown in the lane: assigned when the run starts (also when a finished key starts
   * again), kept through its updates and completion. Higher numbers started later.
   */
  run: number;
}

/** State of {@link reduceProgressLanes}. */
export interface ProgressLanesState {
  lanes: ProgressLane[];
  /** Number of runs started so far; keeps counting when the lanes collapse, so run ids are never reused. */
  runs: number;
}

export const EMPTY_PROGRESS_LANES: ProgressLanesState = { lanes: [], runs: 0 };

/** Maximum number of sub-progress lanes shown at once; beyond that, a new lane replaces the earliest started one. */
export const MAX_PROGRESS_LANES = 5;

/**
 * How long finished lanes stay visible after the last lane change. Lanes of consecutive operations (e.g. several
 * process activations in a row) usually follow each other within moments; removing a lane just to add the next
 * one would make the dialog height flicker. The lanes only collapse once this much time passed without any lane
 * change and all of them are finished.
 */
export const LANE_LINGER_MS = 2000;

/** Internal input of {@link reduceProgressLanes}: the linger time after the last lane change has elapsed. */
export const LANE_LINGER_ELAPSED = "lane-linger-elapsed";

export type ProgressLaneInput = ProgressEventWithId | typeof LANE_LINGER_ELAPSED;

/** `true` for events that belong to the total progress (no sub-progress key). */
export function isTotalProgressEvent(event: ProgressEventWithId): boolean {
  return event.subKey == null;
}

/** `true` for a total progress event reaching 100 %, which ends all sub-progress. */
function isTotalProgressComplete(event: ProgressEventWithId): boolean {
  return isTotalProgressEvent(event) && event.progress != null && event.progress >= 100;
}

/**
 * Reduces the stream of progress events into the list of currently visible sub-progress lanes.
 * - keyed events upsert "their" lane (later values win, missing values keep the previous ones);
 *   a lane never moves backwards - it represents one operation, so a lower value is a nested sub-step
 * - `subDone` keeps the lane but marks it finished (100 %); a new lane takes over the slot of a finished one
 *   instead of growing the list, so back-to-back operations do not change the height
 * - with {@link MAX_PROGRESS_LANES} running lanes, a new lane takes over the slot of the earliest started one
 * - lanes never change their slot, so the remaining lanes do not move
 * - once the linger time has elapsed and all lanes are finished, they are all removed at once
 * - a total event reaching 100 % clears all lanes
 */
export function reduceProgressLanes(state: ProgressLanesState, input: ProgressLaneInput): ProgressLanesState {
  const { lanes, runs } = state;
  if (input === LANE_LINGER_ELAPSED) {
    return lanes.length > 0 && lanes.every((lane) => lane.done) ? { lanes: [], runs } : state;
  }
  const event = input;
  const key = event.subKey;
  if (key == null) {
    return lanes.length > 0 && isTotalProgressComplete(event) ? { lanes: [], runs } : state;
  }
  const existing = lanes.find((lane) => lane.key === key);
  if (event.subDone) {
    return existing
      ? { lanes: lanes.map((lane) => (lane === existing ? { ...lane, progress: 100, done: true } : lane)), runs }
      : state;
  }
  // A finished lane whose key shows up again is a new run of it and must not inherit the old 100 %.
  const running = existing?.done ? undefined : existing;
  const incoming = event.progress ?? running?.progress ?? null;
  const updated: ProgressLane = {
    key,
    label: event.subLabel ?? existing?.label ?? null,
    progress: incoming != null && running?.progress != null ? Math.max(running.progress, incoming) : incoming,
    message: event.progressMessage ?? running?.message ?? null,
    done: false,
    run: running?.run ?? runs + 1,
  };
  const nextRuns = running ? runs : runs + 1;
  const replaced =
    existing ??
    lanes.find((lane) => lane.done) ??
    (lanes.length >= MAX_PROGRESS_LANES
      ? lanes.reduce((earliest, lane) => (lane.run < earliest.run ? lane : earliest))
      : undefined);
  return {
    lanes: replaced ? lanes.map((lane) => (lane === replaced ? updated : lane)) : [...lanes, updated],
    runs: nextRuns,
  };
}

/**
 * Turns the progress events into the stream of visible sub-progress lanes (see {@link reduceProgressLanes}) and
 * collapses finished lanes after {@link LANE_LINGER_MS} without a lane change.
 */
export function trackProgressLanes(
  events$: Observable<ProgressEventWithId>,
  lingerMs: number = LANE_LINGER_MS
): Observable<ProgressLane[]> {
  return events$.pipe(
    // Total progress updates are frequent and say nothing about the lanes, so they must not restart the linger timer.
    filter((event) => !isTotalProgressEvent(event) || isTotalProgressComplete(event)),
    switchMap((event) =>
      concat(of<ProgressLaneInput>(event), timer(lingerMs).pipe(map((): ProgressLaneInput => LANE_LINGER_ELAPSED)))
    ),
    scan(reduceProgressLanes, EMPTY_PROGRESS_LANES),
    map((state) => state.lanes)
  );
}

@Component({
  selector: "new-ui-progress-bar",
  templateUrl: "./new-ui-progress-bar.component.html",
  imports: [ProgressBar],
  animations: [laneBlock, laneReveal, laneSwap],
})
export class NewUiProgressBarComponent {
  progressNotificationService = inject(ProgressNotificationService);

  readonly progressid = input<string>("1");
  readonly initialMessage = input<string>("...");
  readonly mode = input<ProgressMode>("activity-indicator");

  private readonly events$ = this.progressNotificationService.onProgress.pipe(
    filter((x) => x.id === this.progressid())
  );

  /** Message of the total progress; keyed (sub-progress) events never change it. */
  readonly progressMessage: Signal<string | null> = derivedAsync(
    () =>
      this.events$.pipe(
        filter(isTotalProgressEvent),
        map((progress) => (progress.progressMessage != null ? progress.progressMessage : this.progressMessage()))
      ),
    { initialValue: null }
  );

  /** Percentage of the total progress; stays `null` until the first number arrives. */
  readonly progress: Signal<number | null> = derivedAsync(
    () =>
      this.events$.pipe(
        filter(isTotalProgressEvent),
        map((progress) => progress.progress ?? this.progress() ?? null)
      ),
    { initialValue: null }
  );

  readonly primeNgMode = computed<"determinate" | "indeterminate">(() => {
    const isWaitingForFirstNumber = isNil(this.progress());
    const isActivityIndicator = this.mode() === "activity-indicator";
    return isWaitingForFirstNumber && isActivityIndicator ? "indeterminate" : "determinate";
  });

  /** Sub-progress lanes of nested operations (e.g. one process being activated while a model is published). */
  readonly subProgresses: Signal<ProgressLane[]> = derivedAsync(() => trackProgressLanes(this.events$), {
    initialValue: [],
  });

  /** Drives the expand/collapse animation of the (always present) lane block. */
  readonly lanesExpanded = computed(() => this.subProgresses().length > 0);

  /**
   * Lanes in the DOM: keeps the last lanes while the lane block collapses (and stays collapsed), so the block has
   * content to shrink. When new lanes arrive during the collapse, the same block expands again - there is never a
   * second block whose height would add up with the collapsing one.
   */
  readonly renderedLanes = linkedSignal<ProgressLane[], ProgressLane[]>({
    source: this.subProgresses,
    computation: (lanes, previous) => (lanes.length > 0 || previous == null ? lanes : previous.value),
  });
}
